import { useTriggerCRUDActions } from '@mm/state/actions/TriggerCRUDActions';
import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { get } from 'lodash';
import queryString from 'query-string';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import useReactRouter from 'use-react-router';
import CollapsibleContentContainer from '../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialogueSelectorWithReload } from '../../startBotPromoters/common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { StartBotsDialogueEditor } from '../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { EditSMSKeywordHeader } from './EditSMSKeywordHeader/EditSMSKeywordHeader';
import './EditSMSKeywordPanel.pcss';
import { usePrimeState } from './use-prime-state';
import { SmsIphonePreview } from '../../campaigns/components/SmsIphonePreview/SmsIphonePreview';

export const EditSMSKeywordPanel = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const {
    history,
    match: { params },
  } = useReactRouter<{
    chatbotId: string;
    smsKeywordId: string;
    dialogueId: string;
  }>();
  const chatbotId = Number(params.chatbotId);
  const smsKeywordId = Number(params.smsKeywordId);
  const dialogueId = Number(params.dialogueId);

  const triggerCRUDActions = useTriggerCRUDActions();

  const { keywordTrigger, setKeywordTrigger, keywordDialogue, widgets } =
    usePrimeState(chatbotId, smsKeywordId, dialogueId);

  const [nameEditMode, setNameEditMode] = useState(false);

  const onDelete = () => {
    const deleteCallback = async () => {
      if (keywordTrigger) {
        await triggerCRUDActions.delete(keywordTrigger);

        const path = BotBuilderRoutes.chatStarters;

        const kind: UIFilterStarterKindType = 'sms_keywords';

        history.push({
          pathname: path.getLink({ chatbotId }),
          search: queryString.stringify({ kind }),
        });
      }
    };

    dispatch(
      modalActions.openDeleteConfirmModal({
        headerText: 'Are you sure you want to delete this keyword?',
        deleteCallback,
      }),
    );
  };

  const onNameChange = async (name: string) => {
    const phrase = { phrase: name, sortOrder: 1 };
    try {
      const trigger = await triggerCRUDActions.update({
        ...keywordTrigger,
        phrases: [phrase],
      });

      setKeywordTrigger(trigger);
    } catch {}
  };

  const pathGraph = useNewGraphInstance(dialogueId);

  const dialogueSelectorDropdown = useMemo(() => {
    const botResponseDialogueUpdate = async (newDialogueId: number) => {
      if (newDialogueId && keywordTrigger && newDialogueId !== dialogueId) {
        const updatedBotResponses = keywordTrigger.botResponses.map(r =>
          r.pageId === dialogueId ? { ...r, pageId: newDialogueId } : r,
        );

        const updatedKeywordTrigger = {
          ...keywordTrigger,
          botResponses: updatedBotResponses,
        };

        const savedTrigger = await triggerCRUDActions.update(
          updatedKeywordTrigger,
        );

        if (savedTrigger) {
          setKeywordTrigger(savedTrigger);

          const newUrl = BotBuilderRoutes.smsKeywordEdit.getLink({
            chatbotId,
            dialogueId: newDialogueId,
            smsKeywordId: keywordTrigger.id,
          });

          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return dialogueId ? (
      <DialogueSelectorWithReload
        dialogueId={dialogueId}
        entityUpdateFunction={botResponseDialogueUpdate}
      />
    ) : null;
  }, [keywordTrigger, dialogueId, chatbotId]);

  if (keywordTrigger && keywordDialogue) {
    const name = get(keywordTrigger, 'phrases[0].phrase');

    return (
      <div className="edit-sms-keyword-panel">
        <InactiveChannelAlertSmart sms type="SMS Keyword Starter" />

        <EditSMSKeywordHeader
          chatbotId={chatbotId}
          onDelete={onDelete}
          name={name}
          onNameChange={onNameChange}
          nameEditMode={nameEditMode}
          setNameEditMode={setNameEditMode}
        />
        <CollapsibleContentContainer
          orderNumber="1"
          containerName="Choose or create a Dialogue to respond to people who opt in with this keyword"
          className="edit-sms-keyword-panel__step"
        >
          <div className="edit-sms-keyword-panel__dialogue">
            <PathGraphContext.Provider value={pathGraph}>
              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                keyWordsHeader={name}
                keyWordsHeaderOnClick={() => setNameEditMode(true)}
                chatbotId={chatbotId}
                widgets={widgets}
                dialogueId={dialogueId}
                isSmsRelated={true}
                smsOnly={true}
                platforms={['sms']}
                widgetsToExclude={[
                  WidgetTypes.GALLERY,
                  WidgetTypes.OTN,
                  WidgetTypes.TYPING,
                  WidgetTypes.ATTACHMENT,
                ]}
              />
            </PathGraphContext.Provider>
          </div>
          <SmsIphonePreview destinationPageId={dialogueId} testNumber={false} />
        </CollapsibleContentContainer>
      </div>
    );
  }

  return null;
};
